import { useCallback, useMemo } from 'react'
import { z } from 'zod'
import { useForm } from 'react-hook-form'

import type { OrganizationResponse } from '@/api/types/Organization'
import { zodResolver } from '@hookform/resolvers/zod'

interface OrganizationInfoFormType {
  name: OrganizationResponse['organizationDetails']['name']
  cover?: File | null
  previousCover?: string | null
}

const useOrganizationInfoForm = (organization: Partial<OrganizationResponse>) => {
  const defaultValues = useCallback((org: Partial<OrganizationResponse>): OrganizationInfoFormType => ({
    name: org.organizationDetails?.name || '',
    cover: undefined,
    previousCover: org.assets?.COVER,
  }), [])

  const validationSchema = useMemo(() => z.object({
    name: z.string().min(2, 'Name must be at least 2 characters').max(100, 'Name must be at most 100 characters'),
    cover: z.instanceof(File).nullish(),
  }), [])

  const form = useForm<OrganizationInfoFormType>({
    defaultValues: defaultValues(organization),
    resolver: zodResolver(validationSchema)
  })

  const resetForm = useCallback((org: Partial<OrganizationResponse>) => {
    form.reset(defaultValues(org))
  }, [form, defaultValues])

  return { form, resetForm }
}

export default useOrganizationInfoForm
